import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { parseOrdersListSearch } from "@/modules/pos/presentation/pages/orders-list-search";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const SimpleLayout = lazy(() =>
  import("./layout/SimpleLayout").then((module) => ({
    default: module.SimpleLayout,
  })),
);
const POSLayout = lazy(() =>
  import("./layout/POSLayout").then((module) => ({
    default: module.POSLayout,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const BranchDeviceSelectionPage = lazy(() =>
  import("@/modules/pos/presentation/pages/BranchDeviceSelectionPage").then(
    (module) => ({
      default: module.BranchDeviceSelectionPage,
    }),
  ),
);
const POSPage = lazy(() =>
  import("@/modules/pos/presentation/pages/POSPage").then((module) => ({
    default: module.POSPage,
  })),
);
const OrdersPage = lazy(() =>
  import("@/modules/pos/presentation/pages/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  })),
);
const OrderDetailPage = lazy(() =>
  import("@/modules/pos/presentation/pages/OrderDetailPage").then((module) => ({
    default: module.OrderDetailPage,
  })),
);
const KitchenDisplayPage = lazy(() =>
  import("@/modules/pos/presentation/pages/KitchenDisplayPage").then(
    (module) => ({
      default: module.KitchenDisplayPage,
    }),
  ),
);
const BillSettingsPage = lazy(() =>
  import("@/modules/pos/presentation/pages/BillSettingsPage").then(
    (module) => ({
      default: module.BillSettingsPage,
    }),
  ),
);
const CustomerDisplaySettingsPage = lazy(() =>
  import("@/modules/pos/presentation/pages/CustomerDisplaySettingsPage").then(
    (module) => ({
      default: module.CustomerDisplaySettingsPage,
    }),
  ),
);
const SyncDebugPage = lazy(() =>
  import("@/modules/pos/presentation/pages/SyncDebugPage").then((module) => ({
    default: module.SyncDebugPage,
  })),
);
const CustomerDisplayPage = lazy(() =>
  import("@/modules/pos/presentation/pages/CustomerDisplayPage").then(
    (module) => ({
      default: module.CustomerDisplayPage,
    }),
  ),
);
const ShiftsPage = lazy(() =>
  import("@/modules/pos/presentation/pages/ShiftsPage").then((module) => ({
    default: module.ShiftsPage,
  })),
);
const PaymentMethodsReportPage = lazy(() =>
  import("@/modules/pos/presentation/pages/PaymentMethodsReportPage").then(
    (module) => ({
      default: module.PaymentMethodsReportPage,
    }),
  ),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/pos", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const simpleLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/select-branch-device",
  component: SimpleLayout,
});

const posLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/pos",
  component: POSLayout,
});

const posRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <POSPage />
    </LazyPage>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/orders",
  validateSearch: (search: Record<string, unknown>) =>
    parseOrdersListSearch(search),
  component: () => (
    <LazyPage>
      <OrdersPage />
    </LazyPage>
  ),
});

const orderDetailRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/orders/$orderId",
  component: () => (
    <LazyPage>
      <OrderDetailPage />
    </LazyPage>
  ),
});

const kitchenRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/kitchen",
  component: () => (
    <LazyPage>
      <KitchenDisplayPage />
    </LazyPage>
  ),
});

const shiftsRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/shifts",
  component: () => (
    <LazyPage>
      <ShiftsPage />
    </LazyPage>
  ),
});

const paymentMethodsReportRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/reports/payment-methods",
  component: () => (
    <LazyPage>
      <PaymentMethodsReportPage />
    </LazyPage>
  ),
});

const billSettingsRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/settings/bill",
  component: () => (
    <LazyPage>
      <BillSettingsPage />
    </LazyPage>
  ),
});

const customerDisplaySettingsRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/settings/customer-display",
  component: () => (
    <LazyPage>
      <CustomerDisplaySettingsPage />
    </LazyPage>
  ),
});

const syncDebugRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/settings/sync",
  component: () => (
    <LazyPage>
      <SyncDebugPage />
    </LazyPage>
  ),
});

const customerDisplayRoute = createRoute({
  getParentRoute: () => posLayoutRoute,
  path: "/customer-display",
  component: () => (
    <LazyPage>
      <CustomerDisplayPage />
    </LazyPage>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const branchDeviceSelectionRoute = createRoute({
  getParentRoute: () => simpleLayoutRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <BranchDeviceSelectionPage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([profileRoute]),
  simpleLayoutRoute.addChildren([branchDeviceSelectionRoute]),
  posLayoutRoute.addChildren([
    posRoute,
    ordersRoute,
    orderDetailRoute,
    kitchenRoute,
    shiftsRoute,
    paymentMethodsReportRoute,
    customerDisplayRoute,
    billSettingsRoute,
    customerDisplaySettingsRoute,
    syncDebugRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
